import { RadioGroup } from '@mui/material';
import type { VoidFunctionComponent } from 'react';

import { UserOrgRoleManagement, UserTeamAndRoleManagement } from '../../../../../../../shared/organizations/types';
import LabeledCheckbox from '../../../../../generic/Forms/LabeledCheckbox';
import LabeledRadio from '../../../../../generic/Forms/LabeledRadio';
import Text from '../../../../../generic/Text';
import useAuthenticationSettingsContext from '../../hooks/useAuthenticationSettingsContext';

import styles from './UserRoleManagement.module.less';

const UserRoleManagement: VoidFunctionComponent = () => {
  const { settings, onUpdateSAML } = useAuthenticationSettingsContext();

  return (
    <div className={styles.userRoleManagement}>
      <Text.H3>Options</Text.H3>
      <p>
        Here you can configure whether your identity provider or FOSSA manages certain features.
      </p>

      <Text.H4>Organization Roles</Text.H4>
      <RadioGroup
        value={settings.saml.orgRoleManagement}
        onChange={(event) => {
          onUpdateSAML({
            orgRoleManagement: event.target.value as UserOrgRoleManagement,
          });
        }}
      >
        <LabeledRadio
          value={UserOrgRoleManagement.FOSSA}
          label={
            <div className={styles.labelContent}>
              FOSSA controlled
              <span>
                This option allows you to change a user&apos;s role in FOSSA.
                This ignores the SAML <strong>role</strong> attribute.
              </span>
            </div>
          }
        />
        <LabeledRadio
          value={UserOrgRoleManagement.IDP}
          label={
            <div className={styles.labelContent}>
              SAML controlled
              <span>
                This option allows you to specify a user&apos;s organization role via
                the SAML <strong>role</strong> attribute.
              </span>
            </div>
          }
        />
        <LabeledRadio
          value={UserOrgRoleManagement.MIXED}
          label={
            <div className={styles.labelContent}>
              Mixed control
              <span>
                This option allows you to specify a user&apos;s organization role via
                the FOSSA UI or the SAML <strong>role</strong> attribute. However, it is
                not be possible to remove a user&apos;s role via SAML.
              </span>
            </div>
          }
        />
      </RadioGroup>

      <Text.H4>Teams and Team Roles</Text.H4>
      <RadioGroup
        value={settings.saml.teamRoleManagement}
        onChange={(event) => {
          onUpdateSAML({
            teamRoleManagement: event.target.value as UserTeamAndRoleManagement,
          });
        }}
      >
        <LabeledRadio
          value={UserTeamAndRoleManagement.FOSSA}
          label={
            <div className={styles.labelContent}>
              FOSSA controls Teams and Team Roles
              <span>
                This option allows you to specify a user&apos;s team and assign team roles in FOSSA.
              </span>
            </div>
          }
        />
        <LabeledRadio
          value={UserTeamAndRoleManagement.IDP_TEAMS_FOSSA_ROLES}
          label={
            <div className={styles.labelContent}>
              SAML controls Teams, FOSSA controls Team Roles
              <span>
                This option allows you to specify a user&apos;s team via SAML.
                Default team roles and an individual&apos;s team role are managed in FOSSA.
              </span>
            </div>
          }
        />
        <LabeledRadio
          value={UserTeamAndRoleManagement.IDP}
          label={
            <div className={styles.labelContent}>
              SAML controls Teams and Team Roles
              <span>
                This option allows you to specify a user&apos;s team and team role via SAML.
                Assigning users to teams and managing their team roles within FOSSA is disabled.
              </span>
            </div>
          }
        />
        <LabeledRadio
          value={UserTeamAndRoleManagement.MIXED}
          label={
            <div className={styles.labelContent}>
              Mixed control
              <span>
                This option allows you to specify a user&apos;s team and team role via
                the FOSSA UI or SAML. However, it is not possible to remove a user
                from all teams via SAML.
              </span>
            </div>
          }
        />
      </RadioGroup>

      <LabeledCheckbox
        isChecked={settings.saml.createMissingTeams}
        isDisabled={settings.saml.teamRoleManagement === UserTeamAndRoleManagement.FOSSA}
        onChange={(event) => {
          onUpdateSAML({
            createMissingTeams: event.target.checked,
          });
        }}
        label={
          <div className={styles.labelContent}>
            Automatically create missing teams
            <span>
              When enabled, FOSSA will automatically create teams that are specified in SAML
              attributes but don&apos;t exist in the organization yet.
            </span>
          </div>
        }
      />
    </div>
  );
};

export default UserRoleManagement;
